import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api, apiErr, API } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { Plus, Plug, KeyRound, RefreshCw, Ban, Copy, ScrollText, Building2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS = {
  active: { label: "active", cls: "bg-emerald-500/15 text-emerald-400 ring-emerald-500/30", dot: "bg-emerald-400" },
  to_renew: { label: "to_renew", cls: "bg-amber-500/15 text-amber-400 ring-amber-500/30", dot: "bg-amber-400" },
  revoked: { label: "revoked", cls: "bg-rose-500/15 text-rose-400 ring-rose-500/30", dot: "bg-rose-400" },
  unauthorized: { label: "unauthorized", cls: "bg-rose-500/15 text-rose-400 ring-rose-500/30", dot: "bg-rose-400" },
};

function StatusPill({ status, t }) {
  const c = STATUS[status] || STATUS.revoked;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${c.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} /> {t(`intg.${status}`)}
    </span>
  );
}

export default function Integrations() {
  const { t } = useI18n();
  const { isSuperAdmin, effectiveStructure } = useAuth();
  const [items, setItems] = useState([]);
  const [structures, setStructures] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ structure_id: "", booking_structure_id: "" });
  const [creds, setCreds] = useState(null); // one-time client_id/secret
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const load = () => api.get("/integrations/booking").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/structures").then((r) => setStructures(r.data)); }, []);

  const openNew = () => {
    setForm({ structure_id: effectiveStructure || structures[0]?.id || "", booking_structure_id: "" });
    setOpenCreate(true);
  };

  const create = async () => {
    try {
      const { data } = await api.post("/integrations/booking", form);
      setOpenCreate(false);
      setCreds({ ...data, regenerated: false });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const regenerate = async (c) => {
    try {
      const { data } = await api.post(`/integrations/booking/${c.id}/regenerate`);
      setCreds({ ...data, regenerated: true });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const revoke = async (c) => {
    if (!window.confirm(t("intg.confirm_revoke"))) return;
    try { await api.post(`/integrations/booking/${c.id}/revoke`); toast.success("✓"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const showAudit = async (c) => {
    const { data } = await api.get(`/integrations/booking/${c.id}/audit`);
    setAuditRows(data); setAuditFor(c);
  };

  const authorize = async (c) => {
    try { await api.post(`/integrations/booking/${c.id}/authorize`); toast.success("✓"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const tokenTest = async (c) => {
    try {
      const { data } = await api.post(`/integrations/booking/${c.id}/token-test`);
      navigator.clipboard.writeText(data.access_token);
      toast.success(t("intg.token_issued") + " ✓");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success(t("intg.copied")); };
  const tokenUrl = `${API}/booking/oauth/token`;

  return (
    <div data-testid="integrations-page">
      <PageHeader title={t("intg.title")} subtitle={t("intg.subtitle")} action={
        <button data-testid="new-booking-conn-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2.5 text-sm font-semibold transition-colors">
          <Plus className="h-4 w-4" /> {t("intg.generate")}
        </button>} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.id} data-testid={`booking-conn-${c.id}`} className="rounded-xl border border-[#1F2937] bg-[#111827] p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-500/10 ring-1 ring-blue-500/20 flex items-center justify-center"><Plug className="h-5 w-5 text-blue-400" /></div>
                <div>
                  <h3 className="font-display font-semibold text-sm flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-slate-500" /> {c.structure_name}</h3>
                  <p className="text-xs text-slate-500 font-mono">Booking ID: {c.booking_structure_id}</p>
                </div>
              </div>
              <StatusPill status={c.display_status} t={t} />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <Field label={t("intg.client_id")} value={c.client_id} mono onCopy={() => copy(c.client_id)} />
              <Field label={t("intg.client_secret")} value={c.client_secret_masked} mono />
              <Field label={t("intg.token_status")} value={c.token_status} />
              <Field label={t("intg.last_access")} value={c.last_api_access ? new Date(c.last_api_access).toLocaleString() : "—"} />
              <Field label={t("intg.created")} value={c.linked_at ? new Date(c.linked_at).toLocaleDateString() : "—"} />
              <Field label={t("intg.last_renew")} value={c.last_renew_at ? new Date(c.last_renew_at).toLocaleString() : "—"} />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {c.display_status !== "active" && (
                <button data-testid={`authorize-conn-${c.id}`} onClick={() => authorize(c)} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600/15 text-emerald-300 ring-1 ring-emerald-600/30 hover:bg-emerald-600/25 px-3 py-1.5 text-xs font-medium transition-colors"><ShieldCheck className="h-3.5 w-3.5" /> {t("intg.authorize")}</button>
              )}
              <button data-testid={`token-test-${c.id}`} onClick={() => tokenTest(c)} disabled={c.display_status === "revoked" || c.display_status === "unauthorized"} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40"><KeyRound className="h-3.5 w-3.5" /> {t("intg.token_test")}</button>
              <button data-testid={`regen-conn-${c.id}`} onClick={() => regenerate(c)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors"><RefreshCw className="h-3.5 w-3.5" /> {t("intg.regenerate")}</button>
              <button data-testid={`revoke-conn-${c.id}`} onClick={() => revoke(c)} disabled={c.display_status === "revoked"} className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600/15 text-rose-300 ring-1 ring-rose-600/30 hover:bg-rose-600/25 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40"><Ban className="h-3.5 w-3.5" /> {t("intg.revoke")}</button>
              <button data-testid={`audit-conn-${c.id}`} onClick={() => showAudit(c)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors"><ScrollText className="h-3.5 w-3.5" /> {t("intg.audit")}</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-slate-500 py-10 text-center col-span-2">{t("intg.none")}</div>}
      </div>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="font-display">{t("intg.generate")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {isSuperAdmin && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("nav.structures")}</label>
                <select data-testid="conn-structure-select" value={form.structure_id} onChange={(e) => setForm({ ...form, structure_id: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm">
                  {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("intg.booking_id")}</label>
              <input data-testid="conn-booking-id-input" value={form.booking_structure_id} onChange={(e) => setForm({ ...form, booking_structure_id: e.target.value })} placeholder="es. 125" className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpenCreate(false)} className="rounded-lg px-4 py-2 text-sm text-slate-400">{t("common.cancel")}</button>
            <button data-testid="save-conn-btn" onClick={create} className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-semibold">{t("common.create")}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* One-time credentials dialog */}
      <Dialog open={!!creds} onOpenChange={(v) => !v && setCreds(null)}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><KeyRound className="h-5 w-5 text-amber-400" /> {t("intg.client_secret")}</DialogTitle></DialogHeader>
          {creds && (
            <div className="space-y-3">
              <div className="rounded-lg bg-amber-500/10 ring-1 ring-amber-500/20 px-3 py-2 text-xs text-amber-300">{t("intg.secret_once")}</div>
              <SecretRow label={t("intg.client_id")} value={creds.client_id} onCopy={() => copy(creds.client_id)} />
              <SecretRow label={t("intg.client_secret")} value={creds.client_secret} onCopy={() => copy(creds.client_secret)} />
              <SecretRow label={t("intg.token_url")} value={tokenUrl} onCopy={() => copy(tokenUrl)} />
            </div>
          )}
          <div className="flex justify-end">
            <button data-testid="close-creds-btn" onClick={() => setCreds(null)} className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-semibold">{t("common.close")}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit dialog */}
      <Dialog open={!!auditFor} onOpenChange={(v) => !v && setAuditFor(null)}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{t("intg.audit")} — {auditFor?.structure_name}</DialogTitle></DialogHeader>
          <div className="max-h-[420px] overflow-y-auto rounded-lg border border-[#1F2937]">
            <table className="w-full text-xs">
              <thead><tr className="text-left uppercase tracking-wider text-slate-500 border-b border-[#1F2937] sticky top-0 bg-[#111827]">
                <th className="px-3 py-2">API</th><th className="px-3 py-2">{t("common.status")}</th><th className="px-3 py-2">Request ID</th><th className="px-3 py-2">{t("intg.created")}</th>
              </tr></thead>
              <tbody>
                {auditRows.map((a) => (
                  <tr key={a.id} className="border-b border-[#1F2937]/60">
                    <td className="px-3 py-2 font-mono text-cyan-400">{a.api}</td>
                    <td className={`px-3 py-2 font-semibold ${a.result === "accept" ? "text-emerald-400" : "text-rose-400"}`}>{a.result}</td>
                    <td className="px-3 py-2 font-mono text-slate-500">{a.request_id?.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-slate-500">{new Date(a.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {auditRows.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">{t("common.none")}</td></tr>}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, mono, onCopy }) {
  return (
    <div className="rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center justify-between">
        {label}
        {onCopy && <button onClick={onCopy} className="text-slate-500 hover:text-blue-400"><Copy className="h-3 w-3" /></button>}
      </div>
      <div className={`truncate ${mono ? "font-mono text-cyan-400" : "text-slate-300"}`}>{value || "—"}</div>
    </div>
  );
}

function SecretRow({ label, value, onCopy }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-1 flex items-center gap-2 rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2">
        <span className="flex-1 font-mono text-xs text-emerald-300 break-all">{value}</span>
        <button onClick={onCopy} className="text-slate-400 hover:text-blue-400 shrink-0"><Copy className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
